import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft, Phone, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import SectionHeader from "../components/SectionHeader";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";

const Rental = () => {
  const { t } = useTranslation();
  const { siteContent, services } = useSiteData();
  const { pick } = useLocaleText();
  const rental = (services || []).filter((s) => s.type === "rental");
  const phones = siteContent?.phones || ["+974 50871458"];

  const features = [
    t("rental.feat1"), t("rental.feat2"), t("rental.feat3"),
    t("rental.feat4"), t("rental.feat5"), t("rental.feat6"),
  ];

  return (
    <div data-testid="page-rental">
      <SEO title={t("nav.rental")} description={t("rental.subtitle")} path="/services/rental" />

      <section className="relative bg-[#111111] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-services.jpg" alt="RTC heavy equipment" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#111111]/95 via-[#111111]/55 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-8 md:py-12">
          <Link to="/services" className="inline-flex items-center gap-2 font-mono-rtc text-[11px] uppercase tracking-[0.3em] text-[#FF5A00] hover:text-white transition-colors" data-testid="rental-back-link">
            <ArrowLeft size={14} className="rtl:rotate-180" /> {t("common.backToServices")}
          </Link>
          <h1 className="font-display uppercase font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6">
            {t("rental.title")}
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/80">{t("rental.subtitle")}</p>
        </div>
      </section>

      <section className="bg-[#FF5A00]">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f} className="flex items-center gap-3 text-[#111111]">
              <CheckCircle2 size={20} />
              <span className="font-medium uppercase tracking-wide text-sm">{f}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader eyebrow={t("home.fleetEyebrow")} title={t("rental.fleetTitle")} subtitle={t("rental.fleetSubtitle")} />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rental.map((eq, i) => (
              <article key={eq.slug} className="group bg-white border-2 border-[#111111] flex flex-col hover:border-[#FF5A00] block-shadow-to-orange transition-all duration-300" data-testid={`rental-card-${eq.slug}`}>
                <div className="relative aspect-[4/3] overflow-hidden bg-[#F3F4F6]">
                  <img src={eq.image} alt={pick(eq.name)} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700" />
                  {eq.capacity && (
                    <div className="absolute top-3 right-3 bg-[#FF5A00] text-white px-3 py-1 font-mono-rtc text-[10px] uppercase tracking-[0.2em] font-bold">
                      {eq.capacity}
                    </div>
                  )}
                </div>
                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <span className="font-mono-rtc text-[11px] uppercase tracking-[0.2em] text-[#FF5A00]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-2xl sm:text-3xl uppercase font-bold mt-1 group-hover:text-[#FF5A00] transition-colors duration-300">{pick(eq.name)}</h3>
                  <p className="mt-2 text-[#52525B] text-sm leading-relaxed flex-1">{pick(eq.description)}</p>
                  <Link to="/contact" state={{ service: `Rental — ${pick(eq.name)}` }} className="mt-5 inline-flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-[#111111] hover:text-[#FF5A00] transition-colors" data-testid={`rental-quote-${eq.slug}`}>
                    {t("common.requestQuote")} <ArrowRight size={14} className="rtl:rotate-180 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-8 md:py-10 bg-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl md:text-4xl uppercase font-bold">
              {t("rental.urgentTitle")} <br />
              <span className="text-[#FF5A00]">{t("rental.urgentTitle2")}</span>
            </h3>
            <p className="mt-4 text-white/70 max-w-md">{t("rental.urgentSubtitle")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={`tel:${phones[0].replace(/\s/g, "")}`} className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF5A00] text-white px-7 py-4 font-bold uppercase tracking-wide hover:bg-white hover:text-[#111111] transition-colors" data-testid="rental-call-cta">
              <Phone size={16} /> <span dir="ltr">{phones[0]}</span>
            </a>
            <Link to="/contact" className="flex-1 inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white px-7 py-4 font-bold uppercase tracking-wide hover:bg-white hover:text-[#111111] transition-colors" data-testid="rental-contact-cta">
              {t("rental.sendInquiry")} <ArrowRight size={16} className="rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Rental;
